import { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import config from "@/lib/constants/config";
import { showToast } from "@/lib/utils/toast";
import { PATHS } from "@/lib/router";

import { useOnboardingStore } from "../hooks";
import { onboardingService } from "../services";
import { EsgForm, NxmapForm, NxplanForm } from "../components";

const getProjectFromLocation = (location, projectParam) => {
  const fromQuery = location.search
    ? new URLSearchParams(location.search).get("project")
    : "";
  return (projectParam || fromQuery || "").toLowerCase();
};

const buildVisitUrl = (projectName) => {
  const base = String(config.apiUrl || "").replace(/\/$/, "");
  const redirectPath = "/";
  return `${base}/visit/${projectName}?redirect=${encodeURIComponent(redirectPath)}`;
};

const OnboardingPage = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const location = useLocation();
  const { project: projectParam } = useParams();

  const project = useMemo(
    () => getProjectFromLocation(location, projectParam),
    [location, projectParam],
  );

  const { fetchOnboard, saveOnboard } = useOnboardingStore();

  const [submitTick, setSubmitTick] = useState(0);
  const [prevTick, setPrevTick] = useState(0);
  const [skipTick, setSkipTick] = useState(0);
  const [nxplanStep, setNxplanStep] = useState(1);
  const [nxmapStep, setNxmapStep] = useState(1);
  const [pendingAction, setPendingAction] = useState(null);
  const [finishingProject, setFinishingProject] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchOnboard?.().catch((error) => {
      showToast.error(
        error?.message ||
          t("Unable to load onboarding details. Please try again."),
      );
    });
  }, [fetchOnboard, project]);

  const handleSubmit = (action = null) => {
    setPendingAction(action);
    setSubmitTick((tick) => tick + 1);
  };

  const handleSkip = () => setSkipTick((tick) => tick + 1);

  const handleFinish = (targetProject) => {
    if (isSaving) return;
    const projectName = String(targetProject || "").toLowerCase();
    if (!projectName) return;
    setFinishingProject(projectName);
    setPendingAction(`${projectName}-finish`);
    setSubmitTick((tick) => tick + 1);
  };

  const handleFormValid = async (payload) => {
    const projectName = String(
      payload?.project || finishingProject || "",
    ).toLowerCase();
    if (!projectName) {
      setPendingAction(null);
      return;
    }

    setIsSaving(true);
    try {
      const success = await saveOnboard?.(projectName, payload);
      if (success === false) {
        throw new Error(t("Unable to save onboarding details."));
      }

      if (pendingAction === `${projectName}-visit`) {
        if (projectName === "nxmap") {
          const response = await onboardingService.getNxmapCode({
            showErrorToast: false,
          });
          const code = String(response || "").trim();
          if (!code) {
            throw new Error(t("Unable to retrieve NXMap access code."));
          }
          const base = String(config.nxmapBaseUrl || "").replace(/\/$/, "");
          const ssoUrl = `${base}/sso-login?code=${encodeURIComponent(code)}`;
          window.open(ssoUrl, "_blank", "noopener,noreferrer");
        } else {
          window.open(
            buildVisitUrl(projectName),
            "_blank",
            "noopener,noreferrer",
          );
        }
      } else {
        navigate(PATHS.DASHBOARD);
      }
    } catch (error) {
      showToast.error(
        error?.message ||
          t("Unable to save onboarding details. Please try again."),
      );
    } finally {
      setIsSaving(false);
      setFinishingProject(null);
      setPendingAction(null);
    }
  };

  const containerRef = useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const fits = el.scrollHeight <= window.innerHeight;
      document.documentElement.style.overflowY = fits ? "hidden" : "";
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      document.documentElement.style.overflowY = "";
      window.removeEventListener("resize", update);
      ro.disconnect();
    };
  }, []);

  const isNxmap = project === "nxmap";
  const step = isNxmap ? nxmapStep : nxplanStep;
  const projectKey = isNxmap ? "nxmap" : "nxplan";
  const visitKey = isNxmap ? "nxmap-visit" : "nxplan-visit";
  const visitLabel = isNxmap ? t("Visit NXMap") : t("Visit NXPlan");

  const isFinished = step >= 3;
  const isDisabled = isSaving && finishingProject === projectKey;

  return (
    <div
      ref={containerRef}
      className={
        project === "esg" ? "" : "flex min-h-[calc(100vh-130px)] flex-col" // Original classes
      }
    >
      {project === "nxplan" && (
        <NxplanForm
          submitTick={submitTick}
          prevTick={prevTick}
          skipTick={skipTick}
          onValid={handleFormValid}
          setNxplanStep={setNxplanStep}
        />
      )}
      {project === "nxmap" && (
        <NxmapForm
          submitTick={submitTick}
          prevTick={prevTick}
          skipTick={skipTick}
          onValid={handleFormValid}
          setNxmapStep={setNxmapStep}
        />
      )}
      {project === "esg" && <EsgForm />}
      {!["nxmap", "nxplan", "esg"].includes(project) && (
        <div className="rounded-radius border border-border bg-card p-8 text-center text-muted-foreground">
          {t("Select a project to explore.")}
        </div>
      )}

      <div className="sticky bottom-0 left-0 right-0 mt-auto grid grid-cols-[auto,1fr,auto] items-center gap-4 border-t border-border bg-background/80 px-3 py-4 backdrop-blur">
        <div>
          {((project === "nxplan" && nxplanStep > 1) ||
            (project === "nxmap" && nxmapStep > 1)) && (
            <Button
              variant="outline"
              onClick={() => setPrevTick((tick) => tick + 1)}
            >
              {t("Previous")}
            </Button>
          )}
        </div>
        <div className="justify-self-center" />
        <div className="flex items-center gap-2 justify-self-end">
          {project !== "nxplan" && project !== "nxmap" && project !== "esg" && (
            <>
              <Button variant="outline" onClick={() => navigate(-1)}>
                {t("Cancel")}
              </Button>
              <Button
                onClick={() =>
                  handleSubmit(project ? `${project}-visit` : null)
                }
              >
                {t("Continue")}
              </Button>
            </>
          )}

          {(project === "nxplan" || project === "nxmap") && (
            <>
              <Button variant="outline" onClick={handleSkip}>
                {t("Skip")}
              </Button>
              {isFinished && (
                <Button
                  onClick={() => handleFinish(projectKey)}
                  disabled={isDisabled}
                >
                  {isDisabled ? t("Saving...") : t("Finish")}
                </Button>
              )}
              <Button
                variant={isFinished ? "secondary" : "default"}
                onClick={() => handleSubmit(isFinished ? visitKey : null)}
                disabled={isDisabled}
              >
                {isFinished ? visitLabel : t("Continue")}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default OnboardingPage;
